/**
 * Pagina Razionale - 3940.it
 * Mostrata obbligatoriamente in onboarding, disponibile nel drawer.
 * Route: /razionale
 */

'use client'

import { useState } from 'react'

const refs = [
  {
    section: 2,
    title: 'Retrieval practice produces more learning than elaborative studying',
    authors: 'Roediger & Karpicke',
    venue: 'Science, 2011',
    doi: 'https://doi.org/10.1126/science.1199327',
  },
  {
    section: 2,
    title: 'The critical importance of retrieval for learning',
    authors: 'Karpicke & Roediger',
    venue: 'Science, 2008',
    doi: 'https://doi.org/10.1126/science.1152408',
  },
  {
    section: 3,
    title: 'Learning concepts and categories: is spacing the "enemy of induction"?',
    authors: 'Kornell & Bjork',
    venue: 'Psychological Science, 2008',
    doi: 'https://doi.org/10.1111/j.1467-9280.2008.02127.x',
  },
  {
    section: 4,
    title: 'Spaced repetition promotes efficient and effective learning: policy implications for instruction',
    authors: 'Kang, S.H.K.',
    venue: 'Policy Insights from the Behavioral and Brain Sciences, 2016',
    doi: 'https://doi.org/10.1177/2372732215624708',
  },
]

function RefLink({ section }: { section: number }) {
  return (
    <div className="flex flex-col gap-2 mt-4">
      {refs
        .filter((r) => r.section === section)
        .map((r) => (
          <a
            key={r.doi}
            href={r.doi}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 hover:bg-slate-800/60 transition-colors group"
          >
            <span className="text-orange-500 text-xs mt-0.5 shrink-0">→</span>
            <div className="min-w-0">
              <p className="text-slate-200 text-xs font-medium leading-snug group-hover:text-white transition-colors">
                {r.title}
              </p>
              <p className="text-slate-500 text-xs mt-0.5">
                {r.authors} — {r.venue}
              </p>
            </div>
          </a>
        ))}
    </div>
  )
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div className="flex-1 bg-slate-800/50 border border-slate-700 rounded-xl p-3 min-w-0">
      <p className="text-orange-500 text-xl font-black tracking-tight">{n}</p>
      <p className="text-slate-400 text-xs mt-0.5 leading-snug">{label}</p>
    </div>
  )
}

function Divider() {
  return <hr className="border-slate-700/60 my-8" />
}

export default function RazionalePage() {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText('https://3940.it/razionale')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Background pattern — stesso della landing */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZoLTJ2LTRoMnY0em0tNiA2aC0ydi00aDJ2NHptMC02aC0ydi00aDJ2NHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />

      <div className="relative z-10 flex-1 flex flex-col items-center px-4 sm:px-6 py-12 md:py-16">
        <div className="w-full max-w-2xl">

          {/* Link alla landing */}
          <div className="mb-8">
            <a href="https://www.3940.it" className="text-slate-500 hover:text-orange-400 text-sm transition-colors">
              ← 3940.it
            </a>
          </div>

          {/* Header */}
          <div className="mb-10">
            <p className="text-orange-500 text-xs font-medium tracking-widest uppercase mb-3">
              Razionale
            </p>
            <h1 className="text-white text-3xl md:text-4xl font-black tracking-tight leading-tight mb-3">
              Perché <span className="text-orange-500">3940</span> funziona in modo diverso
            </h1>
            <p className="text-slate-400 text-base leading-relaxed">
              Non è un&apos;app di quiz. È un sistema di apprendimento costruito su tre
              principi scientifici distinti, applicati insieme per la prima volta alla
              preparazione dei concorsi pubblici italiani.
            </p>
          </div>

          <Divider />

          {/* Principio 1 */}
          <div className="mb-8">
            <p className="text-orange-500 text-xs font-medium tracking-widest uppercase mb-2">
              Principio 1
            </p>
            <h2 className="text-white text-xl font-bold mb-3">
              La quantità non è la misura della preparazione
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-3">
              Le banche dati tradizionali contengono migliaia di domande ridondanti. Più
              domande non significa più copertura: significa più carico cognitivo, più stress
              e più tempo sprecato su variazioni superficiali dello stesso concetto.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              3940 ha selezionato, per ogni concetto di ogni modulo, esattamente due domande:{' '}
              <span className="text-slate-200">una definitoria</span> (cos&apos;è, chi è, come
              si chiama) e <span className="text-slate-200">una applicativa</span> (quando, se,
              in base a cosa, è lecito). Questo non riduce la copertura — la rende più densa e
              più utile.
            </p>
            <div className="flex gap-2 mt-4">
              <Stat n="~13.000" label="domande nella banca dati originale" />
              <Stat n="~2.250" label="domande selezionate in 3940" />
              <Stat n="−85%" label="volume cognitivo, copertura invariata" />
            </div>
          </div>

          <Divider />

          {/* Principio 2 */}
          <div className="mb-8">
            <p className="text-orange-500 text-xs font-medium tracking-widest uppercase mb-2">
              Principio 2
            </p>
            <h2 className="text-white text-xl font-bold mb-3">
              Il cervello ricorda affermazioni, non interrogativi
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-3">
              Ogni domanda in 3940 viene trasformata in un&apos;affermazione prima di essere
              mostrata come quiz.{' '}
              <span className="text-slate-300 italic">
                &ldquo;D.Lgs. 196/2003 è il Codice protezione dati personali&rdquo;
              </span>{' '}
              diventa l&apos;ancoraggio mnemonico — la domanda verifica se lo ricordi.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Questo allineamento tra forma di studio e forma di memorizzazione riduce il
              carico di traduzione cognitiva che normalmente si produce durante la revisione.
            </p>
            <RefLink section={2} />
          </div>

          <Divider />

          {/* Principio 3 */}
          <div className="mb-8">
            <p className="text-orange-500 text-xs font-medium tracking-widest uppercase mb-2">
              Principio 3
            </p>
            <h2 className="text-white text-xl font-bold mb-3">
              Il linguaggio inutile è il nemico principale
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-3">
              Le domande dei concorsi pubblici italiani contengono in media il 35–40% di testo
              che non porta informazione discriminante: preamboli normativi, subordinate
              ridondanti, referenze intere agli articoli. 3940 le rimuove sistematicamente.
            </p>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 my-4 space-y-3">
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Originale</p>
                <p className="text-slate-400 text-xs leading-relaxed italic">
                  &ldquo;A norma di quanto prevede il secondo comma dell&apos;art. 166, D.Lgs.
                  n. 196/2003, la violazione delle disposizioni di cui all&apos;art. 2-ter (Base
                  giuridica per il trattamento di dati personali effettuato per l&apos;esecuzione
                  di un compito di interesse pubblico…) è soggetta a sanzione amministrativa?&rdquo;
                </p>
              </div>
              <div className="border-t border-slate-700 pt-3">
                <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">3940</p>
                <p className="text-orange-400 text-sm font-medium">
                  Violazione art. 2-ter sanzionata?
                </p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Il contenuto discriminante è intatto. Il carico linguistico si riduce di oltre
              i due terzi.
            </p>
            <RefLink section={3} />
          </div>

          <Divider />

          {/* Principio 4 — Metodo */}
          <div className="mb-8">
            <p className="text-orange-500 text-xs font-medium tracking-widest uppercase mb-2">
              Come si traduce in sessione di studio
            </p>
            <h2 className="text-white text-xl font-bold mb-3">
              Due metodi, gli stessi principi: Moduli o Schede
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-3">
              Con i <span className="text-slate-200">Moduli</span>, ogni concetto arriva in
              sequenza: sintesi, due domande, ripasso attivo degli errori con frequenza
              crescente fino alla padronanza. Con le <span className="text-slate-200">Schede</span>,
              la stessa teoria è organizzata per macro-tema e argomento, con i quiz collegati
              ai punti chiave — utile per chi preferisce muoversi per blocchi tematici più
              ampi, con segnalazione immediata di cosa rivedere.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              In entrambi i casi vale lo stesso principio di fondo: niente studio passivo. Il
              sistema calcola in tempo reale la tua traiettoria di avanzamento, qualunque sia
              il metodo scelto — e puoi cambiare metodo quando vuoi.
            </p>
            <RefLink section={4} />
          </div>

          <Divider />

          {/* Condividi */}
          <div className="flex items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              Condividi con chi studia con te
            </p>
            <div className="flex items-center gap-3">
              {copied && (
                <span className="text-orange-400 text-xs">Link copiato</span>
              )}
              <button
                onClick={handleCopy}
                className="text-sm px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:border-slate-400 hover:text-white transition-colors"
              >
                Copia link
              </button>
            </div>
          </div>

          {/* CTA finale */}
          <div className="mt-10 flex flex-col items-center gap-3 text-center">
            <p className="text-slate-400 text-sm">
              Accesso gratuito fino alla data degli scritti.
            </p>
            <a
              href="https://app.3940.it/signup"
              className="inline-flex items-center justify-center px-7 py-3 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-xl transition-colors text-sm"
            >
              Registrati gratis
            </a>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 pb-6 text-slate-600 text-center">
        <p className="text-sm">© 2026 3940.it</p>
      </div>
    </div>
  )
}